import NavBar from "../components/NavBar";
import { useState, useRef, useEffect } from "react";
import { callOpenAI } from '../services/GetOpenAi'
import { ChatMessage } from "../components/ChatMessage";
import { Box, TextField, IconButton } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { useSelector } from "react-redux";
import { useDispatch } from 'react-redux';
import {add} from "../store/planAtripSlice";
import { RootState } from '../store/store';

type TripDetails = {
    destination: string;
    startDate: string;
    endDate: string;
};

type TripPlanningState = {
    phase: 'initial' | 'gathering_info' | 'complete';
    collectedInfo: Partial<TripDetails>;
    questionCount: number;
};

export default function PlanAtrip(){
    const [input, setInput] = useState("");
    const messages = useSelector((state: RootState) => state.planATrip);
    const [isLoading, setIsLoading] = useState(false);
    const [tripPlanningState, setTripPlanningState] = useState<TripPlanningState>({
        phase: 'initial',
        collectedInfo: {},
        questionCount: 0
    });
    const today = new Date().toISOString().slice(0, 10);
    const currentYear = new Date().getFullYear();
    const bottomRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // Comprehensive date parsing function
     const parseFlexibleDate = (dateStr: string): Date | null => {
        const cleanDateStr = dateStr.trim().toLowerCase();
        const currentDate = new Date();

        // Handle "this week" - Tomorrow to Sunday of current week
        if (cleanDateStr.includes('this week')) {
            const tomorrow = new Date(currentDate);
            tomorrow.setDate(currentDate.getDate() + 1);
            return tomorrow;
        }

        // Handle "next week" - Monday to Sunday of next week
        if (cleanDateStr.includes('next week')) {
            const nextMonday = new Date(currentDate);
            const dayOfWeek = currentDate.getDay();
            const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
            nextMonday.setDate(currentDate.getDate() - daysToSubtract + 7); // Add 7 days for next week
            return nextMonday;
        }

        // Handle "next weekend" - assume next Saturday
        if (cleanDateStr.includes('next weekend')) {
            const nextSaturday = new Date(currentDate);
            const daysUntilSaturday = (6 - currentDate.getDay()) % 7;
            nextSaturday.setDate(currentDate.getDate() + daysUntilSaturday + 7);
            return nextSaturday;
        }

        // Handle "this weekend" - assume this Saturday
        if (cleanDateStr.includes('this weekend')) {
            const thisSaturday = new Date(currentDate);
            const daysUntilSaturday = (6 - currentDate.getDay()) % 7;
            thisSaturday.setDate(currentDate.getDate() + daysUntilSaturday);
            return thisSaturday;
        }

        // Handle month names with ordinals (September 1st, March 15th, etc.)
        const ordinalMatch = dateStr.match(/(\w+)\s+(\d{1,2})(st|nd|rd|th)?/i);
        if (ordinalMatch) {
            const monthName = ordinalMatch[1];
            const day = parseInt(ordinalMatch[2]);
            const dateWithoutOrdinal = `${monthName} ${day}, ${currentYear}`;
            const parsed = new Date(dateWithoutOrdinal);
            if (!isNaN(parsed.getTime())) {
                return parsed;
            }
        }

        // Handle MM/DD, M/D, MM/DD/YY, MM/DD/YYYY formats
        const slashMatch = dateStr.match(/(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?/);
        if (slashMatch) {
            const month = parseInt(slashMatch[1]);
            const day = parseInt(slashMatch[2]);
            let year = currentYear;

            if (slashMatch[3]) {
                year = parseInt(slashMatch[3]);
                if (year < 100) {
                    year += year < 50 ? 2000 : 1900;
                }
            }

            const parsed = new Date(year, month - 1, day);
            if (!isNaN(parsed.getTime())) {
                return parsed;
            }
        }

        // Handle "March 15", "Sept 1", etc. (without ordinals)
        const monthDayMatch = dateStr.match(/(\w+)\s+(\d{1,2})(?!\w)/i);
        if (monthDayMatch) {
            const monthName = monthDayMatch[1];
            const day = parseInt(monthDayMatch[2]);
            const dateStr = `${monthName} ${day}, ${currentYear}`;
            const parsed = new Date(dateStr);
            if (!isNaN(parsed.getTime())) {
                return parsed;
            }
        }

        // Handle "September 2025" (month and year only)
        const monthYearMatch = dateStr.match(/(\w+)\s+(\d{4})/i);
        if (monthYearMatch) {
            const monthName = monthYearMatch[1];
            const year = parseInt(monthYearMatch[2]);
            const parsed = new Date(`${monthName} 1, ${year}`);
            if (!isNaN(parsed.getTime())) {
                return parsed;
            }
        }

        // Try standard Date parsing as fallback
        const standardParsed = new Date(dateStr);
        if (!isNaN(standardParsed.getTime())) {
            return standardParsed;
        }

        return null;
    };

    // Enhanced trip details parser (Note: This is not used in the final implementation)
    const parseTripDetails = (text: string): TripDetails | null => {
        // Remove common words and clean the input
        const cleanText = text.replace(/\b(from|to|in|on|during|for|trip|travel|visit|vacation)\b/gi, ' ').trim();

        // Look for date patterns with various separators
        const datePatterns = [
            // Pattern 1: destination + dates (Paris March 15 to March 22)
            /^(.*?)\s+(.+?)\s+(?:to|-|until|through)\s+(.+?)$/i,
            // Pattern 2: destination from date to date (Paris from March 15 to March 22)
            /^(.*?)\s+from\s+(.+?)\s+(?:to|-|until|through)\s+(.+?)$/i,
            // Pattern 3: dates first (March 15 to March 22 Paris)
            /^(.+?)\s+(?:to|-|until|through)\s+(.+?)\s+(.+?)$/i
        ];

        for (const pattern of datePatterns) {
            const match = text.match(pattern);
            if (match) {
                let destination, startDateStr, endDateStr;

                // Try different interpretations based on which parts look like dates
                const parts = [match[1], match[2], match[3]];
                const dateParts = parts.map(part => parseFlexibleDate(part));

                // Find which parts are valid dates
                const dateIndices = dateParts.map((date, index) => date ? index : -1).filter(i => i !== -1);

                if (dateIndices.length >= 2) {
                    // We have at least 2 dates, use first two as start/end
                    const startIdx = dateIndices[0];
                    const endIdx = dateIndices[1];

                    startDateStr = parts[startIdx];
                    endDateStr = parts[endIdx];

                    // The remaining part should be the destination
                    const remainingIdx = [0, 1, 2].find(i => i !== startIdx && i !== endIdx);
                    destination = remainingIdx !== undefined ? parts[remainingIdx] : '';
                } else if (dateIndices.length === 1) {
                    // Only one clear date found, try to parse the other as relative
                    const dateIdx = dateIndices[0];
                    const otherParts = parts.filter((_, i) => i !== dateIdx);

                    // Try to find destination among remaining parts
                    destination = otherParts.find(part => !parseFlexibleDate(part)) || '';
                    startDateStr = parts[dateIdx];
                    endDateStr = otherParts.find(part => parseFlexibleDate(part)) || '';
                } else {
                    // No clear dates found, continue to next pattern
                    continue;
                }

                const startDate = parseFlexibleDate(startDateStr);
                const endDate = parseFlexibleDate(endDateStr);

                if (startDate && endDate && destination.trim()) {
                    return {
                        destination: destination.trim(),
                        startDate: startDate.toISOString().slice(0, 10),
                        endDate: endDate.toISOString().slice(0, 10)
                    };
                }
            }
        }

        return null;
    };

    // Check for past dates
    const checkPastDates = (startDate: string, endDate: string): string | null => {
        const today = new Date();
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (start < today || end < today) {
            return "It looks like one or more of your dates are in the past. Could you please provide future dates for your trip?";
        }

        if (end <= start) {
            return "Your end date should be after your start date. Could you please provide valid date range?";
        }

        return null;
    };

     // Check if user is requesting a new trip
     const isNewTripRequest = (input: string): boolean => {
        const newTripIndicators = [
            /plan.*trip.*to/i,
            /travel.*to/i,
            /visit/i,
            /itinerary.*for/i,
            /going.*to/i,
            /trip.*to/i,
            /vacation.*in/i,
            /holiday.*in/i,
            /want.*to.*go.*to/i,
            // --- New date-based trip patterns ---
            /\bfrom\s+\w+\s+\d{1,2}\s+(to|-)\s+\w+\s+\d{1,2}\b/i,    // e.g. "from March 15 to March 22"
            /\b\d{1,2}\/\d{1,2}\s*-\s*\d{1,2}\/\d{1,2}\b/i,           // e.g. "3/15 - 3/22" or "9/1 - 9/10"
            /\b\w+\s+\d{1,2}(st|nd|rd|th)?\s+(to|-)\s+\d{1,2}(st|nd|rd|th)?\b/i, // e.g. "September 1st to 10th"
            /\b\d{2}\/\d{2}\/\d{2,4}\s*-\s*\d{2}\/\d{2}\/\d{2,4}\b/i,  // e.g. "09/01/25 - 09/10/25"
            // --- Relative time phrases ---
            /\bnext\s+weekend\b/i,   // e.g. "London next weekend"
            /\bthis\s+weekend\b/i,   // e.g. "Paris this weekend"
            /\bnext\s+week\b/i,      // e.g. "Tokyo next week"
            /\bthis\s+week\b/i       // e.g. "Rome this week"
        ];

        return newTripIndicators.some(pattern => pattern.test(input));
    };


    const handleSend = async () => {
        dispatch(add({ sender: "User", text: input }));
        setInput("");
        setIsLoading(true);

        try {
            if (tripPlanningState.phase === 'initial' ||
            (tripPlanningState.phase === "gathering_info" && isNewTripRequest(input)) ||
            isNewTripRequest(input)
            ) {
                // Initial trip request - validate destination, dates, and ask clarifying questions
                const prompt = `
                You are a smart and friendly travel assistant.
                Today's date is ${today}.
                The current year is ${currentYear}.

                **PHASE 1: VALIDATION & CLARIFICATION**

                Your task is to analyze the user's input and:
                1. **Validate destination**: Ensure it's a real, visitable place
                2. **Validate dates**: Check if dates are provided and future dates
                3. **Ask 3 clarifying questions** from these categories to personalize their trip

                **DESTINATION VALIDATION RULES:**
                1. MUST verify destination is a real, visitable place (city, region, country, landmark, etc.)
                2. Accept major cities (e.g., "Paris", "New York", "Tokyo")
                3. Accept countries/regions (e.g., "Italy", "Tuscany", "French Riviera")
                4. Accept famous landmarks/areas (e.g., "Yellowstone", "Napa Valley", "Scottish Highlands")
                5. Accept general regions if context is clear (e.g., "the mountains", "wine country" with location context)
                6. REJECT fictional places (e.g., "Gotham City", "Hogwarts", "Middle Earth")
                7. REJECT vague terms without context (e.g., just "downtown", "the beach" without city)
                8. REJECT inappropriate destinations (dangerous war zones, restricted areas, etc.)
                9. **ONLY if destination is fictional, inappropriate, or completely vague**, ask: "I'd love to help plan your trip! Could you clarify which specific city, region, or destination you'd like to visit?"
                // 10. **If destination is real but too general**, suggest: "To create the best itinerary, could you be more specific about the location? For example, which city or region within [DESTINATION]?"
                10. Countries are VALID destinations - do NOT ask for more specific locations
                11. Only ask for clarification if destination is truly vague (like "the beach" without context)
                12. Accept countries like "India", "Italy", "Japan" as complete, valid destinations

                **DATE VALIDATION RULES:**
                1. Check if dates are provided in the user's input
                2. If NO dates provided, ask: "When are you planning to travel? Please provide your preferred dates."
                3. If dates are provided but in the past, ask for future dates
                4. If dates are unclear or incomplete, ask for clarification
                5. If dates are provided but NO destination, ask for destination clarification

                **IMPORTANT: Handle relative dates intelligently with PRECISE date calculations:**  // ADDED "with PRECISE date calculations"
                1. "this week" = Tomorrow to Sunday of current week (remaining days of current week)
                2. "next week" = Monday to Sunday of next week (7 days total)
                3. "this weekend" = Saturday-Sunday of current week (2 days total)
                4. "next weekend" = Saturday-Sunday of next week (2 days total)

                **CRITICAL DATE CALCULATION RULES:**
                1. Today is ${today} which is a ${new Date(today).toLocaleDateString('en-US', { weekday: 'long' })}
                2. ** SMART DATE YEAR LOGIC:** Handle different year formats correctly:
                    - **2-digit years (e.g., "09/01/25", "25", "/25")**:
                    - 00-29 = 2000-2029 (e.g., "25" = 2025, "01" = 2001)
                    - 30-99 = 1930-1999 (e.g., "85" = 1985, "99" = 1999)
                    - **4-digit years**: Use as provided (e.g., "2025", "2026")
                    - **NEVER explain the year logic to the user - just apply it naturally**
                3. **SMART DATE YEAR LOGIC:** If user provides dates without a year (e.g., "10/15 - 10/22" or "March 15 to March 22"), follow these rules:
                    - ** AUTOMATICALLY Compare the provided dates with today's date (${today})**
                    - **If the dates would be FUTURE dates in the current year (${currentYear}), use current year**
                    - **If the dates would be PAST dates in the current year (${currentYear}), automatically assume next year (${currentYear + 1})**
                    - **NEVER explain the year logic to the user - just apply it naturally**
                4. **Examples with today being ${today} - AUTOMATIC YEAR DETECTION:**
                - **2-digit year examples:**
                      - "09/01/25 - 09/10/25" = September 1-10, 2025 (25 = 2025)
                      - "12/15/25 - 12/22/25" = December 15-22, 2025 (25 = 2025)
                      - "03/01/26 - 03/08/26" = March 1-8, 2026 (26 = 2026)
                - **No year provided examples:**
                    - "3/15 - 3/22" = March 15-22, ${currentYear + 1} (automatically applied)
                    - "March 15 - March 22" = March 15-22, ${currentYear + 1} (automatically applied)
                    - "10/15 - 10/22" = October 15-22, ${currentYear} (automatically applied)
                    - "December 10 - December 15" = December 10-15, ${currentYear} (automatically applied)
                    - "January 5 - January 10" = January 5-10, ${currentYear + 1} (automatically applied)
                5. **CRITICAL: Compare the MONTH against today's month to determine if it has passed or is still to come**
                6. Count the EXACT number of days between start and end dates
                7. Always verify your date calculations are correct

                **RELATIVE DATE CALCULATIONS (CRITICAL):**
                - **"this weekend"**: Calculate remaining days of current weekend and treat as COMPLETE date range
                - **"next weekend"**: Saturday-Sunday of next week and treat as COMPLETE date range
                - **"this week"**: Calculate remaining days of current week and treat as COMPLETE date range
                - **"next week"**: Monday-Sunday of next week and treat as COMPLETE date range
                - **NEVER ask for end dates when user provides relative dates - they are automatically complete**

                **CLARIFYING QUESTIONS (Ask exactly 3 from these categories):**
                1. **Budget range**: "What's your budget range - are you looking for budget-friendly options, mid-range experiences, or luxury travel?"
                2. **Travel style**: "What pace do you prefer - a relaxed leisurely trip, moderate activity level, or packed with lots of activities?"
                3. **Food preferences**: "Do you have any dietary restrictions or food preferences? Are you excited to try local cuisine?"
                4. **Interests**: "What interests you most - culture and history, nature and outdoors, food experiences, nightlife, shopping, or art and museums?"

                **RESPONSE LOGIC:**
                1. **If destination is INVALID (fictional, inappropriate, or completely vague)**: Ask for destination clarification and STOP
                2. **If dates are PROVIDED but destination is MISSING**: Ask for destination clarification and STOP
                3. **If destination is VALID but dates are MISSING**: Ask for dates and STOP
                4. **If destination is VALID and dates are PROVIDED/CALCULATED (including relative dates)**: Proceed with the 3 clarifying questions

                DESTINATION ACCEPTANCE RULES:
                - Cities = Valid and specific enough ✓
                - Countries = Valid and specific enough ✓
                - Regions = Valid and specific enough ✓
                - Landmarks = Valid and specific enough ✓
                - Only reject vague terms without geographic context

                **RESPONSE FORMAT:**
                If destination and dates are valid:
                "Great! I'd love to help you plan your trip to [DESTINATION] from [START DATE] to [END DATE]. To create the perfect personalized itinerary, I have a few questions:

                1. [Question from category 1]
                2. [Question from category 2]
                3. [Question from category 3]"

                **FOR SINGLE DAY TRIPS (like "this weekend" = 1 day):**
                "Great! I'd love to help you plan your day trip to [DESTINATION] on [DATE]. To create the perfect personalized itinerary, I have a few questions:

                1. [Question from category 1]
                2. [Question from category 2]
                3. [Question from category 3]"

                **CRITICAL FORMATTING RULES:**
                - **When dates have been automatically calculated with year, present them naturally without explaining the logic**
                - **Example: "Great! I'd love to help you plan your trip to Paris from March 15 to March 22, 2026."**
                - **DO NOT say things like "Since the dates you've provided, March 15 to March 22, 2026, are in the future" or explain why you chose 2026**
                - **Just present the dates naturally as if the user provided them with the year**
                - **For relative dates, present the calculated dates naturally without asking for confirmation**
                - **For single-day trips, use "day trip" format instead of asking for end dates**

                **CRITICAL: Always ask ALL 3 questions together in the numbered format above - never ask just one question at a time**

                If destination is valid but dates are missing:
                "Great choice! [DESTINATION] is a wonderful place to visit. When are you planning to travel? Please provide your preferred dates so I can create the perfect itinerary for you."

                If dates are valid but destination is missing:
                "I'd love to help you plan your trip for [DATES]! Where are you thinking of traveling to? Please let me know your destination so I can create the perfect itinerary."

                **Rules:**
                1. BEFORE analyzing the current message, FIRST scan through ALL previous messages in the conversation history
                2. Look for dates in ANY previous message, not just the current one
                3. Look for destinations in ANY previous message, not just the current one
                4. If dates were provided in an earlier message and destination is provided now, proceed to 3 clarifying questions
                5. If destination was provided earlier and dates are provided now, proceed to 3 clarifying questions
                6. Only ask for missing information if it's truly missing from the ENTIRE conversation
                2. Validate destination - only reject if truly invalid/fictional/inappropriate
                3. Countries are valid destinations - do NOT ask for more specificity
                3. If destination is valid but dates missing from ALL messages, ask for dates (don't ask clarifying questions yet)
                4. If dates are provided but destination missing from ALL messages, ask for destination (don't ask clarifying questions yet)
                5. If destination is valid and dates provided but in past (dates that have completely passed based on today being ${today}), ask for future dates
                6. **For relative dates (this weekend, next weekend, this week, next week): AUTOMATICALLY calculate the specific dates and treat them as COMPLETE PROVIDED dates - proceed directly to 3 clarifying questions**
                7. **If user says "this week", calculate ONLY remaining days in current week - NEVER extend into next week**
                8. If user says "next week", consider 7-days (Monday-Sunday of next week)
                9. **For dates without year: AUTOMATICALLY apply smart year detection - - NEVER explain or ask for year clarification**
                10. **Present automatically calculated dates naturally without explaining the logic**
                11. CRITICAL: Always double-check your date calculations - count days carefully
                12. ** CRITICAL: If today is Saturday Aug 30:**
                    - "this weekend" = Sunday Aug 31 ONLY (1 day remaining) - PROCEED TO 3 QUESTIONS
                    - "next weekend" = Saturday Sep 6 to Sunday Sep 7 (full next weekend)  - PROCEED TO 3 QUESTIONS
                    - "this week" = Sunday Aug 31 ONLY (1 day remaining) - PROCEED TO 3 QUESTIONS
                    - "next week" = Monday Sep 1 to Sunday Sep 7 (full next week) - PROCEED TO 3 QUESTIONS
                13. Always calculate the specific dates for each day
                14. **PROCEED TO 3 CLARIFYING QUESTIONS when BOTH destination and dates are valid/calculated - NEVER ask for additional date information when relative dates are provided**

                **CRITICAL**: If dates are in the past or destination is invalid, DO NOT ask the 3 clarifying questions yet. Wait for the user to provide valid information first.

                **DO NOT EXPLAIN YEAR LOGIC**: When you automatically apply year logic, present the dates naturally without any explanation about why you chose that year.

                **CRITICAL FOR RELATIVE DATES**: When user says "this weekend", "next weekend", "this week", or "next week", calculate the dates automatically and proceed directly to the 3 clarifying questions. DO NOT ask for end dates or confirmation.

                Current conversation:
                    ${messages.map(m => `${m.sender}: ${m.text}`).join("\n")}

                User input: "${input}"
                `;

                const aiResponse = await callOpenAI(prompt);
                dispatch(add({ sender: "System", text: aiResponse }));

               // Check if AI asked exactly 3 clarifying questions
                const questionMatches = aiResponse.match(/\n\s*\d\.\s+/g) || [];

                if (questionMatches.length === 3) {
                // Only now move to gathering_info phase
                setTripPlanningState({
                    phase: 'gathering_info',
                    collectedInfo: {},
                    questionCount: 3
                });
                }

            }else if (tripPlanningState.phase === 'gathering_info' && !isNewTripRequest(input)) {
                // Continue gathering information or generate itinerary
                const conversationHistory = messages.map(m => `${m.sender}: ${m.text}`).join("\n");

                const prompt = `
                You are a smart and friendly travel assistant.
                Today's date is ${today}.
                The current year is ${currentYear}.

                **PHASE 2: GENERATE ITINERARY**

                Analyze the conversation history to determine if you have enough information to create a comprehensive itinerary. You should have:
                ✅ Valid destination
                ✅ Valid dates
                ✅ User preferences from the 3 questions asked

                If you have sufficient information, generate a detailed itinerary using this EXACT format:

                Hello! Based on our conversation, here's your personalized itinerary for [DESTINATION] from [START DATE] to [END DATE], tailored to your preferences for [mention their stated preferences].

                **Day 1: [Date] - [Theme/Activity]**
                    - Morning: [Activity details] at [Specific venue/location]
                    - Lunch: [Restaurant name and cuisine type] - [Brief description]
                    - Afternoon: [Activity details] at [Specific venue/location]
                    - Coffee Break: [Cafe name] - [Brief description/specialty]
                    - Evening: [Activity details]
                    - Dinner: [Restaurant name and cuisine type] - [Brief description and why it's recommended]

                **Day 2: [Date] - [Theme/Activity]**
                   - Morning: [Activity details] at [Specific venue/location]
                   - Lunch: [Restaurant name and cuisine type] - [Brief description]
                   - Afternoon: [Activity details] at [Specific venue/location]
                   - Coffee Break: [Cafe name] - [Brief description/specialty]
                   - Evening: [Activity details]
                   - Dinner: [Restaurant name and cuisine type] - [Brief description and why it's recommended]

                [Continue for each day...]

                **Day X: [Date] - Departure**
                   - Morning: [Final activities]
                   - Breakfast/Brunch: [Restaurant/cafe name] - [Brief description]    // ✅ NEW: Final meal recommendation
                   - [Departure preparations]

                I hope you have a wonderful trip! Feel free to ask me about restaurants, transportation, or any other travel questions.

                **Rules:**
                1. Adjust restaurant recommendations based on budget range (budget-friendly/mid-range/luxury)
                2. Match activity pace to their stated travel style (relaxed/moderate/packed)
                3. Include food experiences that match their dietary preferences
                4. Focus activities on their stated interests (culture/nature/food/nightlife/shopping)
                5. Always calculate and display the specific dates for each day
                6. Use the exact structure with bolded day headers and bullet points
                7. Include breakfast, lunch, dinner, and coffee break suggestions for each day
                8. Recommend restaurants and cafes that are popular, well-reviewed, or locally famous based on user prefernce
                9. Consider the location of activities when suggesting nearby dining options
                10. Consider that weekdays vs weekends may have different operating hours/availability
                11. For relative dates (this weekend, next weekend), calculate the specific dates based on today being ${today}

                If you still need clarification on any aspect, ask specific follow-up questions.

                Conversation history:
                ${conversationHistory}

                New user response: "${input}"
                `;

                const aiResponse = await callOpenAI(prompt);
                dispatch(add({ sender: "System", text: aiResponse }));

                // Check if response contains an itinerary
                if (aiResponse.includes("Day 1")) {
                    setTripPlanningState(prev => ({ ...prev, phase: 'complete' }));
                }
            }else{
                //Follow-up questions after itinerary is complete
                const prompt = `
                You are a friendly travel assistant.
                The user has already received their itinerary. Now respond to follow-up questions about their travel plans.

                IMPORTANT:
                - Only answer travel-related questions about their trip, destination, restaurants, activities, transportation, etc.
                - If asked about unrelated topics, respond: "I'm here to help with your travel plans — feel free to ask anything related to your trip!"

                Conversation history:
                ${messages.map(m => `${m.sender}: ${m.text}`).join("\n")}

                New question: "${input}"`;

                const aiResponse = await callOpenAI(prompt);
                dispatch(add({ sender: "System", text: aiResponse }));
            }
        } catch (e: any) {
            dispatch(add({ sender: "System", text: `Error: ${e.message || e}` }));
        }finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: `calc(100 * var(--vh))` }}>
            <NavBar />
            <Box
                sx={{
                    flex: 1, // Takes remaining space after NavBar
                    display: "flex",
                    flexDirection: "column",
                    overflowY: "auto",
                    padding: "16px",
                    paddingBottom: "80px", // Accounts for mobile browser bar
                    alignItems: "center",
                }}
            >

                <Box
                    sx={{
                        flexGrow: 1,
                        overflowY: "auto",
                        width: "100%",
                        maxWidth: {
                            xs: "70%",
                            sm: "70%",
                            md: "70%",
                            lg: "700px",
                        },
                        paddingBottom: "16px",
                        paddingRight: "10px",
                        scrollbarWidth: "none",
                    }}
                >
                    {messages.map((msg, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: "flex",
                                justifyContent: msg.sender === "User" ? "flex-end" : "flex-start",
                                mb: 1,
                            }}
                        >
                            <ChatMessage text={msg.text} sender={msg.sender} />
                        </Box>
                    ))}
                    {isLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "flex-start",  mb: 1 }}>
                            Loading...
                        </Box>
                    ): <></>}
                    <div ref={bottomRef} />
                </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: "white",
                            position: "fixed",
                            bottom: 0,
                            border: "1px solid #e0e0e0",
                            borderRadius: "50px",
                            padding: "4px 12px",
                            boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                            width: "100%",
                            maxWidth: {
                                xs: "80%",
                                sm: "80%",
                                md: "80%",
                                lg: "800px",
                            },
                            marginBottom: "calc(30px + var(--safe-area-inset-bottom))", // Accounts for mobile browser bar
                        }}
                    >
                        <TextField
                            variant="standard"
                            fullWidth
                            placeholder="Ask anything..."
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: "16px",
                                    color: "black",
                                    "&::placeholder": {
                                        color: "#888",
                                        opacity: 1,
                                    },
                                    padding: "8px",
                                },
                            }}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        <IconButton onClick={handleSend} sx={{ color: "#555", padding: "8px" }}>
                            <ArrowUpwardIcon />
                        </IconButton>
                    </Box>

            </Box>
        </div>
    );
}
